import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Form from 'components/Form';
import WhatToSeeBlocksField from 'components/WhatToSeeBlocksField';

import { WhatToSeeEditExtra_whatToSee$key } from '__generated__/WhatToSeeEditExtra_whatToSee.graphql';
import { WhatToSeeEditExtraMutation } from '__generated__/WhatToSeeEditExtraMutation.graphql';

type WhatToSeeBlockFormSection = {
  id: string;
  name: string;
  description: string;
};

type FieldType = WhatToSeeBlockFormSection[];

type FormFields = {
  blocks: FieldType;
};

type Props = {
  whatToSee: WhatToSeeEditExtra_whatToSee$key;
};

const WhatToSeeEditExtra: FC<Props> = props => {
  const whatToSee = useFragment(
    graphql`
      fragment WhatToSeeEditExtra_whatToSee on WhatToSee {
        id

        blocks(first: 2147483647) {
          edges {
            node {
              id
              name
              description
            }
          }

          ...WhatToSeeBlocksField_blocksConnection
        }

        ...WhatToSeeBlocksField_whatToSee
      }
    `,
    props.whatToSee
  );

  const [update, updating] = useMutation<WhatToSeeEditExtraMutation>(graphql`
    mutation WhatToSeeEditExtraMutation(
      $id: ID!
      $input: [UpdateWhatToSeeBlocksInput!]!
    ) {
      updateWhatToSeeBlocks(id: $id, input: $input) {
        ...WhatToSeeEditExtra_whatToSee
        ...WhatToSeeSidebar_whatToSee
      }
    }
  `);

  const { id, blocks } = whatToSee;

  const initial = {
    blocks:
      blocks?.edges.map(({ node }) => ({
        ...node,
        description: node.description ?? '',
      })) ?? [],
  };

  const handleSubmit = useCallback(
    (values: FormFields) => {
      update({
        variables: {
          id,
          input: values.blocks.map(({ id, ...input }) => ({
            id,
            input,
          })),
        },
      });
    },
    [id, update]
  );

  if (!blocks) return null;

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <WhatToSeeBlocksField
          name="blocks"
          blocksConnection={blocks}
          whatToSee={whatToSee}
        />
      </Form>
    </Formik>
  );
};

export default WhatToSeeEditExtra;
